// A low-poly wooden rowing boat — a simple open trough / dinghy. Hull is an
// 8-sided open cylinder scaled into an oval, so the cross-section reads as a
// proper bowl from every angle: wide at the gunwale, narrower at the keel,
// visibly deep. Bow faces +z. Ported 1:1 from age-of-mcp's classic boat.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const PLANK = "#a07040";   // warm amber planking
const KEEL  = "#6b4820";   // darker keel & floor
const TRIM  = "#4a2e10";   // dark walnut gunwale & seat

// Cylinder segments — 8 gives a clean octagonal low-poly silhouette.
const SEGS = 8;

// Hull radii (before scale)
const R_TOP = 0.30;   // radius at gunwale — wide brim
const R_BOT = 0.21;   // radius at keel — tapered = flared sides
const H     = 0.22;   // hull depth (tall enough to look like a real boat)

// Axis scales that turn the circle into a boat oval
const SX = 0.60;   // squish beam
const SZ = 1.60;   // stretch fore-aft

function partsFor(): Part[] {
  const parts: Part[] = [];

  // Hull sides: open cylinder — only the lateral surface, so the top stays
  // open while the bowl shape is clear from the sides. Scaled into an oval.
  parts.push({
    geometry: new THREE.CylinderGeometry(R_TOP, R_BOT, H, SEGS, 1, true),
    color: PLANK,
    position: [0, H / 2, 0],
    scale: [SX, 1, SZ],
  });

  // Floor (keel plank): solid disk closing the bottom.
  parts.push({
    geometry: new THREE.CylinderGeometry(R_BOT, R_BOT, 0.030, SEGS),
    color: KEEL,
    position: [0, 0.015, 0],
    scale: [SX, 1, SZ],
  });

  // Gunwale cap: thin solid disk sitting on the top rim.
  parts.push({
    geometry: new THREE.CylinderGeometry(R_TOP + 0.012, R_TOP + 0.012, 0.026, SEGS),
    color: TRIM,
    position: [0, H - 0.013, 0],
    scale: [SX, 1, SZ],
  });

  // Thwart (rowing bench): a single plank across the beam, just below the gunwale.
  parts.push({
    geometry: new THREE.BoxGeometry(R_TOP * 2 * SX * 0.75, 0.026, 0.080),
    color: PLANK,
    position: [0, H * 0.72, 0.06],
  });

  // Bow post: a small upright stem rising above the gunwale at the bow.
  parts.push({
    geometry: new THREE.BoxGeometry(0.040, H * 0.55, 0.040),
    color: TRIM,
    position: [0, H * 1.22, R_TOP * SZ * 0.90],
    rotation: [-0.38, 0, 0],   // rake forward slightly
  });

  return parts;
}

export const boat = defineModel({
  id: "boat",
  batchId: "standard",
  name: "Boat",
  tags: ["vehicle","boat","low-poly","age-of-mcp","classic","water"],
  summary: "Low-poly boat.",
  description: "Ported from the age-of-mcp gallery.",
  author: "age-of-mcp",
  footprint: 0.42,
  parts: () => partsFor(),
  animations: [],
});
